import React, { useState, useEffect, useCallback } from 'react';
import { Layers, Plus, Save, Loader2, Info } from 'lucide-react';

import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Button from '../components/ui/Button';
import SearchableSelectBox from '../components/ui/SearchableSelectBox';

import { useToast } from '../hooks/useToast';
import { client, fetchOrganizations, fetchUtilAccounts, fetchAssetCategories } from '../apiHelper';

const DEPRECIATION_METHODS = [
    { label: "Straight Line", value: "STRAIGHT_LINE" },
    { label: "Declining Balance", value: "DECLINING_BALANCE" },
    { label: "Sum-of-the-Years' Digits", value: "SUM_OF_YEARS_DIGITS" },
];

export const AssetCategoryManager = ({ currentUser }) => {
    const { showToast } = useToast();
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [organizations, setOrganizations] = useState([]);
    const [glAccounts, setGlAccounts] = useState([]);

    const [formObject, setFormObject] = useState({
        organization_id: '',
        code: '',
        name: '',
        depreciation_method: 'STRAIGHT_LINE',
        default_useful_life_months: 60,
        default_salvage_value_pct: 0.00,
        asset_account_id: '',
        accum_depr_account_id: '',
        depr_expense_account_id: ''
    });

    const loadMasterData = useCallback(async () => {
        if (!currentUser) return;
        setLoading(true);
        try {
            const [orgs, accounts] = await Promise.all([
                fetchOrganizations().catch(() => []),
                fetchUtilAccounts(currentUser).catch(() => [])
            ]);
            const processedOrgs = orgs.map(o => ({
                label: o.organization_name,
                value: o.organization_id.toString()
            }))
            const myOrg = processedOrgs.find(o => o.value === currentUser.org_id);
            setOrganizations([myOrg]);

            setGlAccounts(accounts.map(a => ({
                label: `${a.account_code ? a.account_code + ' - ' : ''}${a.name}`,
                value: a.account_id.toString()
            })));
        } catch (err) {
            showToast("Failed to load accounting chart of accounts.", "error");
        } finally {
            setLoading(false);
        }
    }, [currentUser, showToast]);

    useEffect(() => {
        loadMasterData();
    }, [loadMasterData]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormObject(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formObject.organization_id || !formObject.code || !formObject.name) {
            showToast("Organization, Category Code, and Name are required.", "error");
            return;
        }

        if (!formObject.asset_account_id || !formObject.accum_depr_account_id || !formObject.depr_expense_account_id) {
            showToast("All three General Ledger mapping accounts are required.", "error");
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                organization_id: parseInt(formObject.organization_id, 10),
                code: formObject.code.trim().toUpperCase(),
                name: formObject.name.trim(),
                depreciation_method: formObject.depreciation_method,
                default_useful_life_months: parseInt(formObject.default_useful_life_months, 10),
                default_salvage_value_pct: parseFloat(formObject.default_salvage_value_pct || 0),
                asset_account_id: parseInt(formObject.asset_account_id, 10),
                accum_depr_account_id: parseInt(formObject.accum_depr_account_id, 10),
                depr_expense_account_id: parseInt(formObject.depr_expense_account_id, 10),
            };

            await client.postData("/fixed-assets/create-category", payload);
            showToast("Fixed Asset Category configured successfully.", "success");
            setFormObject({
                organization_id: '',
                code: '',
                name: '',
                depreciation_method: 'STRAIGHT_LINE',
                default_useful_life_months: 60,
                default_salvage_value_pct: 0.00,
                asset_account_id: '',
                accum_depr_account_id: '',
                depr_expense_account_id: ''
            });
        } catch (err) {
            showToast(err.response?.data?.detail || "Failed to configure asset category.", "error");
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) return <div className="p-8 text-center text-zinc-500"><Loader2 className="animate-spin inline mr-2 text-indigo-600" /> Loading GL Accounts & Master Data...</div>;

    return (
        <Card className="p-6 border-zinc-200/60 shadow-sm bg-white">
            <div className="border-b border-zinc-100 pb-3 mb-6 flex justify-between items-center">
                <div>
                    <h2 className="text-lg font-bold text-zinc-900 flex items-center gap-2">
                        <Layers className="text-indigo-600" size={20} /> Configure Asset Category & GL Mapping
                    </h2>
                    <p className="text-xs text-zinc-500">Define asset groups and set up GL account rules for automated depreciation postings.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Select
                        name="organization_id"
                        label="Organization"
                        value={formObject.organization_id}
                        onChange={handleInputChange}
                        options={organizations}
                        placeholder="Select organization"
                        required
                    />
                    <Input
                        name="code"
                        label="Category Code"
                        placeholder="e.g., IT-EQP, HEAVY-MACH"
                        value={formObject.code}
                        onChange={handleInputChange}
                        required
                    />
                    <Input
                        name="name"
                        label="Category Name"
                        placeholder="e.g., IT Hardware & Equipment"
                        value={formObject.name}
                        onChange={handleInputChange}
                        required
                    />
                </div>

                {/* Depreciation Defaults */}
                <div className="p-4 bg-zinc-50 rounded-lg border border-zinc-200 space-y-4">
                    <h3 className="text-xs font-bold text-zinc-600 uppercase tracking-wider">Depreciation Defaults</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Select
                            name="depreciation_method"
                            label="Depreciation Method"
                            value={formObject.depreciation_method}
                            onChange={handleInputChange}
                            options={DEPRECIATION_METHODS}
                            required
                        />
                        <Input
                            name="default_useful_life_months"
                            label="Default Useful Life (Months)"
                            type="number"
                            value={formObject.default_useful_life_months}
                            onChange={handleInputChange}
                            required
                        />
                        <Input
                            name="default_salvage_value_pct"
                            label="Default Salvage Value (%)"
                            type="number"
                            step="0.01"
                            value={formObject.default_salvage_value_pct}
                            onChange={handleInputChange}
                        />
                    </div>
                </div>

                {/* GL Accounts Routing */}
                <div className="p-4 bg-indigo-50/40 rounded-lg border border-indigo-100 space-y-4">
                    <h3 className="text-xs font-bold text-indigo-900 uppercase tracking-wider flex items-center gap-1.5">
                        <Info size={14} className="text-indigo-600" /> Automated General Ledger Account Mappings
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <SearchableSelectBox
                            label="Fixed Asset GL Account (Debit)"
                            options={glAccounts}
                            value={formObject.asset_account_id}
                            onChange={(e) => setFormObject(p => ({ ...p, asset_account_id: e.target.value }))}
                            placeholder="Select Gross Asset GL..."
                        />
                        <SearchableSelectBox
                            label="Accumulated Depr. GL (Credit)"
                            options={glAccounts}
                            value={formObject.accum_depr_account_id}
                            onChange={(e) => setFormObject(p => ({ ...p, accum_depr_account_id: e.target.value }))}
                            placeholder="Select Accum. Depr. GL..."
                        />
                        <SearchableSelectBox
                            label="Depreciation Expense GL (Debit)"
                            options={glAccounts}
                            value={formObject.depr_expense_account_id}
                            onChange={(e) => setFormObject(p => ({ ...p, depr_expense_account_id: e.target.value }))}
                            placeholder="Select Expense GL..."
                        />
                    </div>
                </div>

                <div className="flex justify-end pt-2">
                    <Button type="submit" disabled={submitting} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs px-6 py-2">
                        {submitting ? <Loader2 size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1 inline" />} Save Category
                    </Button>
                </div>
            </form>
        </Card>
    );
};